/**
 * Save / Edit filter preset modal with optional sharing and date range.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';

import { isStatsSavedFilterRollingPreset } from '@trafficpulse/shared';

import { Button } from '@/components/ui';
import { isoToDmy, todayIsoDateParis } from '@/features/smswiser-campaigns/utils/dateFormat';

import { SaveFilterModalShell } from './SaveFilterModalShell';
import { SaveFilterModalMainFields } from './SaveFilterModalMainFields';
import type { SaveFilterModalProps } from './SaveFilterModal.types';

export type { SaveFilterSubmitData, SaveFilterModalInitial } from './SaveFilterModal.types';

export const SaveFilterModal: FC<SaveFilterModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
  submitting,
  currentTab,
  currentDateRangeDmy,
  inferredRangePreset,
  initial,
}) => {
  const isEdit = !!initial;
  const [name, setName] = useState('');
  const [color, setColor] = useState('');
  const [tabs, setTabs] = useState<string[]>([]);
  const [shareWith, setShareWith] = useState<Set<string>>(new Set());
  const [includeRollingRange, setIncludeRollingRange] = useState(false);
  const [includeFixedRange, setIncludeFixedRange] = useState(false);
  const [untilToday, setUntilToday] = useState(false);

  const todayDmy = useMemo(() => isoToDmy(todayIsoDateParis()), [open]);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? '');
    setColor(initial?.color || '#3b82f6');
    setTabs(initial?.tabs ?? [currentTab]);
    setShareWith(new Set());
    const hasRolling =
      initial?.dateRangePreset != null &&
      initial.dateRangePreset !== '' &&
      isStatsSavedFilterRollingPreset(initial.dateRangePreset);
    const hasFixed =
      !!(initial?.dateFrom && (initial?.dateTo || initial?.dateToUntilToday));
    setIncludeRollingRange(hasRolling);
    setIncludeFixedRange(hasFixed && !hasRolling);
    setUntilToday(!!initial?.dateToUntilToday);
  }, [open, initial, currentTab]);

  const onIncludeRollingChange = useCallback((v: boolean) => {
    setIncludeRollingRange(v);
    if (v) setIncludeFixedRange(false);
  }, []);

  const onIncludeFixedChange = useCallback((v: boolean) => {
    setIncludeFixedRange(v);
    if (v) {
      setIncludeRollingRange(false);
      return;
    }
    setUntilToday(false);
  }, []);

  const toggleTab = (key: string) => {
    setTabs((prev) =>
      prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key],
    );
  };

  const toggleUser = (id: string) => {
    setShareWith((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!name.trim() || tabs.length === 0) return;
    const rollingKey =
      includeRollingRange && inferredRangePreset !== 'custom'
        ? inferredRangePreset
        : null;
    await onSubmit({
      name: name.trim(),
      color,
      tabs,
      shareUserIds: [...shareWith],
      dateRangePreset: rollingKey,
      includeFixedDateRange: includeFixedRange,
      dateFrom: includeFixedRange ? currentDateRangeDmy.from : null,
      dateTo:
        includeFixedRange && !untilToday ? currentDateRangeDmy.to : null,
      dateToUntilToday: includeFixedRange && untilToday,
    });
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <SaveFilterModalShell onClose={() => onOpenChange(false)}>
      <div className="flex items-center justify-between px-5 py-3.5">
        <h2 className="text-[13px] font-semibold tracking-tight">
          {isEdit ? 'Edit filter preset' : 'Save filter preset'}
        </h2>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="rounded-md p-1 text-muted-foreground/60 hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <SaveFilterModalMainFields
        name={name}
        onNameChange={setName}
        color={color}
        onColorChange={setColor}
        tabs={tabs}
        onToggleTab={toggleTab}
        shareWith={shareWith}
        onToggleUser={toggleUser}
        inferredRangePreset={inferredRangePreset}
        includeRolling={includeRollingRange}
        onIncludeRollingChange={onIncludeRollingChange}
        includeFixed={includeFixedRange}
        onIncludeFixedChange={onIncludeFixedChange}
        untilToday={untilToday}
        onUntilTodayChange={setUntilToday}
        currentDateRangeDmy={currentDateRangeDmy}
        todayDmy={todayDmy}
      />

      <div className="flex items-center justify-end gap-2 border-t px-5 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onOpenChange(false)}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={submitting || !name.trim() || tabs.length === 0}
        >
          {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Save preset'}
        </Button>
      </div>
    </SaveFilterModalShell>
  );
};
